import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Accordion as BootstrapAccordion } from 'react-bootstrap';
import { AccordionSelectCallback } from 'react-bootstrap/esm/AccordionContext';

import type { IAccordion } from '@/components/common/types';

import { classList, lineClamp } from '@/helpers';
import { ChevronRight } from '@/assets/icons';
import { otherColors } from '@/styles/colors';
import { Z_INDEX_ACCORDION_HEADER_SLOT } from '@/config/variables';
import { media } from '@/styles/theme';
import AccordionCustomToggle from '@/components/common/AccordionCustomToggle';

const TreeFolderAccordion = (props: IAccordion): React.ReactElement => {
  const {
    title,
    children,
    onSelectCallback,
    className,
    headerSlot,
    eventKey = 0,
    wrapWithParent = true,
    isChildrenSelected,
    icon,
  } = props;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const collapseRef = useRef<any>(null); // react-bootsprap Collapse ref
  const toggleRef = useRef<HTMLDivElement>(null);

  const handleSelect: AccordionSelectCallback = eventKey => {
    setIsOpen(eventKey !== null);

    if (!(isChildrenSelected && collapseRef.current && !collapseRef.current.props.in && !isOpen)) {
      onSelectCallback?.();
    }
  };

  useEffect(() => {
    if (isChildrenSelected && collapseRef.current && !collapseRef.current.props.in && !isOpen) {
      toggleRef.current?.click();
    }
  }, [isOpen, isChildrenSelected]);

  const AccordionContent = (
    <>
      <AccordionCustomToggle as={AccordionTitle} eventKey={`${eventKey}`} className={className} ref={toggleRef}>
        <>
          <div className={classList(isOpen && 'accordion-open', 'accordion-title w-100')}>
            <img src={icon || ChevronRight} className={classList(!icon && 'accordion-chevron')} />
            <span>{title}</span>
          </div>
          <div className="accordion-header-slot">{headerSlot}</div>
        </>
      </AccordionCustomToggle>
      <BootstrapAccordion.Collapse eventKey={`${eventKey}`} ref={collapseRef}>
        <>{children}</>
      </BootstrapAccordion.Collapse>
    </>
  );

  if (!wrapWithParent) {
    return (
      <div className="accordion-item" onClick={(e: React.SyntheticEvent<unknown>) => handleSelect(undefined, e)}>
        {AccordionContent}
      </div>
    );
  }

  return <BootstrapAccordion onSelect={handleSelect}>{AccordionContent}</BootstrapAccordion>;
};

export { TreeFolderAccordion };

const AccordionTitle = styled.div.attrs({
  className: 'w-100 d-flex align-items-center justify-content-between fw-bold',
})`
  padding: 8px 7px;
  border-radius: 8px;
  cursor: pointer;

  ${media.phone} {
    padding: 12px 7px;
  }

  &:hover {
    background-color: #e9ecef;
  }

  span {
    flex-grow: 1;
    ${lineClamp(2)};
  }

  .accordion-header-slot {
    z-index: ${Z_INDEX_ACCORDION_HEADER_SLOT};
    display: none;
  }

  .accordion-title {
    display: flex;
    flex-direction: row !important;
  }

  .accordion-chevron {
    min-width: 20px;
  }

  &.accordion-title-selected {
    position: relative;
    span {
      color: ${otherColors.lxc.blue};
      font-weight: 700;
    }

    &::before {
      content: '';
      right: 0;
      position: absolute;
      height: 100%;
      width: 100%;
      border-radius: 8px;
      background: ${otherColors.lxc.blue};
      opacity: 0.1;
    }

    .accordion-header-slot {
      display: block;
    }
  }
`;
